// src/components/rankings/CreatorRankings.js
//
// Creator draft rankings — ported from
// apps/web-app/src/components/draftRoom/DraftRoomRankings.js, with all league
// coupling stripped: no league context, no getChoicesData stats, no target
// list size. The creator ranks one universal list of as many players as they
// like from the full element pool; unranked players are submitted with a null
// rank (the server stores 0 as its "unranked" sentinel, and 0 from the server
// is treated as unranked on load).
import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Plus,
  Trash2,
  Loader2,
  GripVertical,
  SlidersHorizontal,
  ArrowUpToLine,
  CheckCircle2,
  ListPlus,
  ListRestart,
  ListX,
} from 'lucide-react';
import { toast } from 'sonner';
import { List } from 'react-window';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Card } from 'components/ui/card';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from 'components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'components/ui/dialog';
import { cn } from 'lib/utils';
import { getDfcDraftRankings, submitDraftRankings } from 'services/api';
import { POSITIONS } from 'utils/constants';
import { getLogo } from 'utils/imageUtils';
import { useSaveShortcut, useUnsavedWarning } from 'components/studio/SaveBar';
import { MultiSelectPill } from './FilterPillSelects';

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

const SOURCES = ['OFPL Draft'];

/* The web-app offers extra "Rest by" sort stats from getChoicesData and
   creator ranks — both are league/community coupled and stripped here. The
   only ranking on the getDfcDraftRankings elements themselves is
   `fpl_draft_rank`, so the pool order is fixed to it. */
const RANK_LABEL = 'FPL Rank';

/* Pool comparator: FPL draft rank ascending, players without one sink to the
   bottom. */
/* fpl_draft_rank is draft-bootstrap-only — null for every player while data
   is served from the classic API (preseason). Fall back to name order. */
const byFplRank = (a, b) =>
  (a.fpl_draft_rank || 9999) - (b.fpl_draft_rank || 9999) ||
  (a.web_name || '').localeCompare(b.web_name || '');

const getRankValue = (el) => el.fpl_draft_rank || null;

const POOL_ROW_HEIGHT = 64;

/* Subscribe to a CSS media query — boolean output. Inlined from the web-app's
   hooks/useMediaQuery (the creator app has no hooks directory). Breakpoints
   match tailwind.config.js: sm 600 · md 900 · lg 1200 · xl 1536. */
function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/* Rank number chip — tap to type an exact position (works even while the
   list is filtered, where drag is unavailable). */
const RankNumber = ({ rank, maxRank, onSetRank, playerName }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  /* Guards both Escape-then-blur and Enter-then-blur from double-committing. */
  const doneRef = useRef(false);

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(false);
    const n = parseInt(value, 10);
    if (Number.isFinite(n) && n !== rank) onSetRank(n);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          doneRef.current = false;
          setValue(String(rank));
          setEditing(true);
        }}
        className="shrink-0 min-w-[34px] h-7 px-1 rounded-token-md border border-neutral-300 text-sm text-neutral-600 hover:border-brand-buttonActive hover:text-brand-buttonActive"
        aria-label={`${playerName} is ranked ${rank}. Tap to set a new rank`}
        title="Tap to set rank"
      >
        {rank}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      type="number"
      inputMode="numeric"
      min={1}
      max={maxRank}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          doneRef.current = true;
          setEditing(false);
        }
      }}
      className="h-7 w-14 px-1 text-sm shrink-0"
      aria-label="New rank"
    />
  );
};

const RankedCard = memo(
  ({ el, team, moveToUnranked, onSetRank, maxRank, isFlashing, dragDisabled, dragHandle }) => {
    const logo = team ? getLogo(team.name) : undefined;
    return (
      <Card
        className={cn(
          'flex items-center justify-between p-2 transition-colors duration-700',
          isFlashing && 'bg-sky-100 border-brand-buttonActive'
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <RankNumber
            rank={el.dfc_rank}
            maxRank={maxRank}
            onSetRank={(n) => onSetRank(el, n)}
            playerName={el.web_name}
          />
          {logo && (
            <img src={logo} alt={team.short_name} className="w-6 h-6 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-sm truncate">{el.web_name}</span>
              <Badge
                variant="secondary"
                className="h-[18px] px-1 text-[10px] font-normal bg-neutral-200 text-neutral-700 border-transparent"
              >
                {el.singular_name_short}
              </Badge>
            </div>
            <p className="text-xs text-neutral-500">{`${RANK_LABEL}: ${getRankValue(el) || '-'}`}</p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => moveToUnranked(el)}
            aria-label={`Remove ${el.web_name} from rankings`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {!dragDisabled && (
            <button
              type="button"
              ref={dragHandle.setActivatorNodeRef}
              {...dragHandle.attributes}
              {...dragHandle.listeners}
              className="touch-none shrink-0 h-9 w-8 flex items-center justify-center rounded text-neutral-400 cursor-grab active:cursor-grabbing hover:bg-neutral-100"
              aria-label={`Drag to reorder ${el.web_name}`}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
        </div>
      </Card>
    );
  }
);

const SortableRankedItem = memo(
  ({ el, team, moveToUnranked, onSetRank, maxRank, isFlashing, disabled }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: el.element, disabled });

    const style = {
      transform: CSS.Transform.toString(
        transform ? { ...transform, x: 0 } : transform
      ),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 10 : undefined,
      position: 'relative',
    };

    return (
      <div ref={setNodeRef} style={style} id={`ranked-row-${el.element}`}>
        <RankedCard
          el={el}
          team={team}
          moveToUnranked={moveToUnranked}
          onSetRank={onSetRank}
          maxRank={maxRank}
          isFlashing={isFlashing}
          dragDisabled={disabled}
          dragHandle={{ attributes, listeners, setActivatorNodeRef }}
        />
      </div>
    );
  }
);

const UnrankedCard = memo(({ el, team, moveToRanked }) => {
  const logo = team ? getLogo(team.name) : undefined;
  return (
    <Card className="flex items-center justify-between p-2 h-full">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {logo && (
          <img src={logo} alt={team.short_name} className="w-6 h-6 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-sm truncate">{el.web_name}</span>
            <Badge
              variant="secondary"
              className="h-[18px] px-1 text-[10px] font-normal bg-neutral-200 text-neutral-700 border-transparent"
            >
              {el.singular_name_short}
            </Badge>
          </div>
          <p className="text-xs text-neutral-500">{`${RANK_LABEL}: ${getRankValue(el) || '-'}`}</p>
        </div>
      </div>

      <div className="flex items-center gap-0.5 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => moveToRanked(el, 'start')}
          aria-label={`Add ${el.web_name} to top of rankings`}
          title="Add to top"
        >
          <ArrowUpToLine className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => moveToRanked(el, 'end')}
          aria-label={`Add ${el.web_name} to rankings`}
          title="Add to end"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
});

/* Virtualized row for the player pool (react-window v2). */
const PoolRow = memo(({ index, style, ariaAttributes, items, teamMap, moveToRanked }) => {
  const el = items[index];
  if (!el) return null;
  return (
    <div style={style} {...ariaAttributes} className="pr-1">
      <div className="py-1 h-full">
        <UnrankedCard
          el={el}
          team={teamMap.get(el.team_fplID)}
          moveToRanked={moveToRanked}
        />
      </div>
    </div>
  );
});

export default function CreatorRankings({ user }) {
  const isMobile = useMediaQuery('(max-width: 599px)');

  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('OFPL Draft');
  const [ranked, setRanked] = useState([]);
  const [unranked, setUnranked] = useState([]);
  const [filterTeams, setFilterTeams] = useState([]);
  const [filterPositions, setFilterPositions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileView, setMobileView] = useState('rankings');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'replace' | 'clear' | null
  const [flashId, setFlashId] = useState(null);

  const flashTimer = useRef(null);
  /* Pre-bulk-action snapshot so the toast's Undo can restore both lists. */
  const snapshotRef = useRef(null);
  /* Mirrors loadingSubmit for the Cmd/Ctrl+S handler without re-binding it. */
  const submittingRef = useRef(false);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const wpId = user?.wp_id ?? user?.user?.wp_id;
    if (!wpId) return setError('Missing wp_id');
    setLoadingData(true);

    getDfcDraftRankings(wpId, source)
      .then((rankingsRes) => {
        setData(rankingsRes);

        /* dfc_rank 0 from the server is its "unranked" sentinel. */
        const rankMap = new Map(
          (rankingsRes.dfc_rankings || []).map((r) => [r.element, r.dfc_rank])
        );
        const annotated = (rankingsRes.elements || []).map((el) => {
          const rawRank = rankMap.get(el.element);
          return { ...el, dfc_rank: rawRank === 0 ? null : rawRank ?? null };
        });

        const r = annotated
          .filter((el) => el.dfc_rank !== null)
          .sort((a, b) => a.dfc_rank - b.dfc_rank);
        const u = annotated.filter((el) => el.dfc_rank === null).sort(byFplRank);

        setRanked(r);
        setUnranked(u);
        setSubmitSuccess(false);
        setDirty(false);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingData(false));
  }, [user, source]);

  /* Warn before the tab closes with unsaved rankings. */
  useUnsavedWarning(dirty);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const flash = useCallback((elementId) => {
    setFlashId(elementId);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashId(null), 2000);
  }, []);

  /* When switching to the rankings view with a freshly added player, bring it
     into view. */
  useEffect(() => {
    if (mobileView !== 'rankings' || !flashId) return;
    requestAnimationFrame(() => {
      document
        .getElementById(`ranked-row-${flashId}`)
        ?.scrollIntoView({ block: 'center' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileView]);

  const teamOptions =
    data?.teams?.map((t) => ({
      label: t.short_name,
      value: t.team_fplID,
      logoUrl: getLogo(t.name),
    })) || [];
  const positionOptions = POSITIONS.map((pos) => ({ label: pos, value: pos }));
  const teamMap = new Map((data?.teams || []).map((t) => [t.team_fplID, t]));

  const summaryCounts = ranked.reduce(
    (acc, p) => {
      if (acc[p.singular_name_short] !== undefined) acc[p.singular_name_short]++;
      return acc;
    },
    { GKP: 0, DEF: 0, MID: 0, FWD: 0 }
  );

  const searchLower = searchQuery.toLowerCase();
  const filteredRanked = ranked.filter(
    (p) =>
      (!filterTeams.length || filterTeams.includes(p.team_fplID)) &&
      (!filterPositions.length || filterPositions.includes(p.singular_name_short)) &&
      (!searchQuery || p.web_name.toLowerCase().includes(searchLower))
  );
  const filteredUnranked = unranked.filter(
    (p) =>
      (!filterTeams.length || filterTeams.includes(p.team_fplID)) &&
      (!filterPositions.length || filterPositions.includes(p.singular_name_short)) &&
      (!searchQuery || p.web_name.toLowerCase().includes(searchLower))
  );

  const sortDisabled =
    filterTeams.length > 0 || filterPositions.length > 0 || searchQuery.length > 0;

  const activeFilterCount =
    (filterTeams.length ? 1 : 0) + (filterPositions.length ? 1 : 0);

  const markDirty = useCallback(() => {
    setDirty(true);
    setSubmitSuccess(false);
  }, []);

  const moveToRanked = useCallback(
    (player, where = 'end') => {
      const newRank = where === 'start' ? 1 : ranked.length + 1;
      markDirty();
      setUnranked((u) => u.filter((p) => p.element !== player.element));
      setRanked((r) => {
        if (r.some((p) => p.element === player.element)) return r;
        const next = where === 'start' ? [player, ...r] : [...r, player];
        return next.map((p, i) => ({ ...p, dfc_rank: i + 1 }));
      });
      flash(player.element);
      toast.success(
        `${player.web_name} added at #${newRank}`,
        isMobile
          ? { action: { label: 'View', onClick: () => setMobileView('rankings') } }
          : undefined
      );
      requestAnimationFrame(() => {
        document
          .getElementById(`ranked-row-${player.element}`)
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
    [ranked.length, markDirty, flash, isMobile]
  );

  const moveToUnranked = useCallback(
    (player) => {
      markDirty();
      setRanked((r) => {
        const next = r.filter((p) => p.element !== player.element);
        return next.map((p, i) => ({ ...p, dfc_rank: i + 1 }));
      });
      setUnranked((u) => [...u, { ...player, dfc_rank: null }].sort(byFplRank));
    },
    [markDirty]
  );

  const onDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;
      markDirty();
      setRanked((r) => {
        const oldIndex = r.findIndex((p) => p.element === active.id);
        const newIndex = r.findIndex((p) => p.element === over.id);
        return arrayMove(r, oldIndex, newIndex).map((p, i) => ({ ...p, dfc_rank: i + 1 }));
      });
    },
    [markDirty]
  );

  const onSetRank = useCallback(
    (player, newRank) => {
      markDirty();
      setRanked((r) => {
        const idx = r.findIndex((p) => p.element === player.element);
        if (idx === -1) return r;
        const target = Math.min(Math.max(newRank, 1), r.length) - 1;
        if (target === idx) return r;
        return arrayMove(r, idx, target).map((p, i) => ({ ...p, dfc_rank: i + 1 }));
      });
      flash(player.element);
      requestAnimationFrame(() => {
        document
          .getElementById(`ranked-row-${player.element}`)
          ?.scrollIntoView({ block: 'nearest' });
      });
    },
    [markDirty, flash]
  );

  const undoBulk = useCallback(() => {
    const snap = snapshotRef.current;
    if (!snap) return;
    snapshotRef.current = null;
    markDirty();
    setRanked(snap.ranked);
    setUnranked(snap.unranked);
  }, [markDirty]);

  /* Append every pool player that has an FPL rank, in FPL-rank order. There
     is no league-derived target size — the creator's list can be as long as
     they like. Players with no FPL rank are never imported. */
  const rankRest = useCallback(() => {
    const sorted = [...unranked].sort(byFplRank);
    const toAdd = sorted.filter((p) => getRankValue(p) != null);
    if (!toAdd.length) {
      toast.error(`No pool players have a ${RANK_LABEL} value`);
      return;
    }
    snapshotRef.current = { ranked, unranked };
    markDirty();
    const added = new Set(toAdd.map((p) => p.element));
    setRanked([...ranked, ...toAdd].map((p, i) => ({ ...p, dfc_rank: i + 1 })));
    setUnranked(sorted.filter((p) => !added.has(p.element)));
    toast.success(`Added ${toAdd.length} players by ${RANK_LABEL}`, {
      action: { label: 'Undo', onClick: undoBulk },
    });
  }, [ranked, unranked, markDirty, undoBulk]);

  /* Throw away the current order and rebuild the list as all players with an
     FPL rank, in FPL-rank order. Confirmed via dialog. */
  const replaceAll = useCallback(() => {
    const sorted = [...ranked, ...unranked].sort(byFplRank);
    const top = sorted.filter((p) => getRankValue(p) != null);
    setConfirmAction(null);
    if (!top.length) {
      toast.error(`No players have a ${RANK_LABEL} value`);
      return;
    }
    snapshotRef.current = { ranked, unranked };
    markDirty();
    const chosen = new Set(top.map((p) => p.element));
    setRanked(top.map((p, i) => ({ ...p, dfc_rank: i + 1 })));
    setUnranked(
      sorted.filter((p) => !chosen.has(p.element)).map((p) => ({ ...p, dfc_rank: null }))
    );
    toast.success(`List replaced with ${top.length} players by ${RANK_LABEL}`, {
      action: { label: 'Undo', onClick: undoBulk },
    });
  }, [ranked, unranked, markDirty, undoBulk]);

  /* Empty the list back into the pool. Confirmed via dialog. */
  const clearList = useCallback(() => {
    setConfirmAction(null);
    if (!ranked.length) return;
    snapshotRef.current = { ranked, unranked };
    markDirty();
    setRanked([]);
    setUnranked(
      [...unranked, ...ranked.map((p) => ({ ...p, dfc_rank: null }))].sort(byFplRank)
    );
    toast.success('List cleared', { action: { label: 'Undo', onClick: undoBulk } });
  }, [ranked, unranked, markDirty, undoBulk]);

  const handleSubmitRankings = useCallback(() => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoadingSubmit(true);
    /* Full element list: ranked players 1..N, unranked null (the server
       stores null as its 0 sentinel) — same payload as the web-app. */
    const combined = [...ranked, ...unranked].map((p) => ({
      element: p.element,
      dfc_rank: p.dfc_rank ?? null,
    }));
    const wpId = user?.wp_id ?? user?.user?.wp_id;
    submitDraftRankings(wpId, source, combined)
      .then(() => {
        setSubmitSuccess(true);
        setDirty(false);
        toast.success('Rankings saved');
      })
      .catch((err) => toast.error(err.message || 'Failed to save rankings'))
      .finally(() => {
        submittingRef.current = false;
        setLoadingSubmit(false);
      });
  }, [ranked, unranked, user, source]);

  /* Cmd/Ctrl+S saves while there are unsaved changes. */
  useSaveShortcut(handleSubmitRankings, dirty && !loadingData);

  if (error) return <p className="text-brand-destructive mt-4">{error}</p>;

  const filterPills = (
    <>
      <MultiSelectPill
        label="Teams"
        options={teamOptions}
        selected={filterTeams}
        onChange={setFilterTeams}
      />

      <MultiSelectPill
        label="Positions"
        options={positionOptions}
        selected={filterPositions}
        onChange={setFilterPositions}
      />
    </>
  );

  const summaryBadges = (
    <div className="flex gap-1.5 flex-wrap">
      {Object.entries(summaryCounts).map(([pos, c]) => (
        <Badge
          key={pos}
          variant="secondary"
          className="bg-neutral-200 text-neutral-700 border-transparent"
        >
          {pos}: {c}
        </Badge>
      ))}
    </div>
  );

  /* How many players "Rank the rest" would add: every pool player with an
     FPL rank. */
  const importableCount = unranked.reduce(
    (n, p) => (getRankValue(p) != null ? n + 1 : n),
    0
  );

  const bulkActions = (
    <div className="flex flex-wrap gap-2 mb-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={rankRest}
        disabled={!importableCount}
        title={`Append the rest of the pool in ${RANK_LABEL} order`}
      >
        <ListPlus className="h-4 w-4" />
        Rank the rest (+{importableCount})
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setConfirmAction('replace')}
        title={`Replace your list with all players in ${RANK_LABEL} order`}
      >
        <ListRestart className="h-4 w-4" />
        Replace my list
      </Button>
    </div>
  );

  const clearButton = ranked.length > 0 && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="gap-1.5 text-neutral-500 hover:text-brand-destructive"
      onClick={() => setConfirmAction('clear')}
    >
      <ListX className="h-4 w-4" />
      Clear
    </Button>
  );

  const rankedList = (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEnd}
      collisionDetection={closestCenter}
    >
      <SortableContext
        items={ranked.map((el) => el.element)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-2">
          {filteredRanked.map((el) => (
            <SortableRankedItem
              key={el.element}
              el={el}
              team={teamMap.get(el.team_fplID)}
              moveToUnranked={moveToUnranked}
              onSetRank={onSetRank}
              maxRank={ranked.length}
              isFlashing={flashId === el.element}
              disabled={sortDisabled}
            />
          ))}
          {filteredRanked.length === 0 && (
            <div className="text-sm text-neutral-500 p-4 text-center">
              {ranked.length === 0 ? (
                <>
                  <p className="mb-3">No players ranked yet — add them from the player pool.</p>
                  <div className="flex justify-center gap-2 flex-wrap">
                    {isMobile && (
                      <Button variant="outline" size="sm" onClick={() => setMobileView('pool')}>
                        Browse player pool
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={rankRest}>
                      <ListPlus className="h-4 w-4" />
                      Fill with {RANK_LABEL}
                    </Button>
                  </div>
                </>
              ) : (
                'No ranked players match your filters.'
              )}
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );

  const poolList = (height) => (
    <div style={{ height }}>
      {filteredUnranked.length === 0 ? (
        <p className="text-sm text-neutral-500 p-4 text-center">
          No players match your search or filters.
        </p>
      ) : (
        <List
          rowComponent={PoolRow}
          rowCount={filteredUnranked.length}
          rowHeight={POOL_ROW_HEIGHT}
          rowProps={{
            items: filteredUnranked,
            teamMap,
            moveToRanked,
          }}
          overscanCount={6}
        />
      )}
    </div>
  );

  return (
    /* Mobile: a flex column filling the space below the tabs — toolbar rows
       take natural height, the active list is flex-1 and resizes with the
       viewport (no JS measurement). Desktop: normal flow. */
    <div className="flex flex-col h-full min-h-0 sm:h-auto sm:block">
      {SOURCES.length > 1 && (
        <ToggleGroup
          type="single"
          value={source}
          onValueChange={(val) => val && setSource(val)}
          className="mb-4"
        >
          {SOURCES.map((label) => (
            <ToggleGroupItem key={label} value={label}>
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}

      {/* Toolbar: search, filters, view switch, ranked count. Fixed in the
          flex column on mobile; sticky within the scrolling page on
          desktop. */}
      <div className="shrink-0 sm:sticky sm:top-0 z-20 bg-background sm:-mx-5 sm:px-5 py-2 mb-3 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <Input
            type="search"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 flex-1 sm:max-w-xs"
          />
          {isMobile ? (
            <Button
              type="button"
              variant="outline"
              className="h-9 shrink-0 gap-1.5"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="h-5 min-w-5 px-1 justify-center bg-brand-buttonActive text-white border-transparent">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">{filterPills}</div>
          )}
        </div>

        {isMobile && (
          <ToggleGroup
            type="single"
            value={mobileView}
            onValueChange={(v) => v && setMobileView(v)}
            className="mt-2 flex w-full"
          >
            <ToggleGroupItem value="rankings" className="flex-1">
              My rankings ({ranked.length})
            </ToggleGroupItem>
            <ToggleGroupItem value="pool" className="flex-1">
              Player pool
            </ToggleGroupItem>
          </ToggleGroup>
        )}

        <p className="mt-2 text-xs text-neutral-500 whitespace-nowrap">
          {`${ranked.length} player${ranked.length === 1 ? '' : 's'} ranked · rest by ${RANK_LABEL}`}
        </p>
      </div>

      {/* Mobile filter bottom sheet */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="p-4 pb-8">
          <SheetHeader className="text-left pb-2">
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>
              Filter the player pool and your ranked list.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">{filterPills}</div>
            <div>
              <p className="text-sm font-medium mb-1.5">Your ranked positions</p>
              {summaryBadges}
            </div>
            <div className="flex gap-2">
              {(activeFilterCount > 0 || searchQuery) && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setFilterTeams([]);
                    setFilterPositions([]);
                    setSearchQuery('');
                  }}
                >
                  Clear filters
                </Button>
              )}
              <Button type="button" className="flex-1" onClick={() => setFiltersOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {loadingData ? (
        <div className="flex justify-center my-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand-buttonActive" />
        </div>
      ) : isMobile ? (
        /* ---- Mobile: one full-width view at a time; the list takes all
           remaining height. pb clearance keeps the last rows reachable above
           the fixed save bar. ---- */
        <div className="flex-1 min-h-0 flex flex-col">
          {mobileView === 'rankings' ? (
            <>
              {ranked.length > 0 && (
                <div className="flex justify-end -mt-1 mb-1 shrink-0">{clearButton}</div>
              )}
              {sortDisabled && filteredRanked.length > 0 && (
                <p className="text-xs text-neutral-500 mb-2 shrink-0">
                  Drag is paused while filters are active — tap a rank number to
                  move a player.
                </p>
              )}
              <div
                className={cn(
                  'flex-1 min-h-0 overflow-y-auto overflow-x-hidden',
                  dirty && 'pb-24'
                )}
              >
                {rankedList}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-neutral-500 mb-2 shrink-0">
                Your list completes in this order ({RANK_LABEL}). Tap + to add.
              </p>
              <div className="shrink-0">{bulkActions}</div>
              <div className={cn('flex-1 min-h-0', dirty && 'pb-20')}>
                {poolList('100%')}
              </div>
            </>
          )}
        </div>
      ) : (
        /* ---- Desktop / tablet: two columns ---- */
        <div className="grid sm:grid-cols-2 gap-6 max-w-[950px]">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <p className="text-base font-medium">My rankings ({ranked.length})</p>
              <div className="flex items-center gap-2">
                {summaryBadges}
                {clearButton}
              </div>
            </div>
            {sortDisabled && filteredRanked.length > 0 && (
              <p className="text-xs text-neutral-500 mb-2">
                Drag is paused while filters are active — click a rank number to
                move a player.
              </p>
            )}
            <div className="max-h-[600px] overflow-y-auto overflow-x-hidden p-2 border border-neutral-300 rounded-token-md">
              {rankedList}
            </div>
          </div>

          <div className="flex flex-col min-w-0">
            <p className="text-base font-medium mb-2">Player pool</p>
            <p className="text-xs text-neutral-500 mb-2">
              Your list completes in this order ({RANK_LABEL}).
            </p>
            {bulkActions}
            <div className="p-2 border border-neutral-300 rounded-token-md">
              {poolList('600px')}
            </div>
          </div>
        </div>
      )}

      {/* Replace / clear confirmation */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'clear' ? 'Clear your list?' : 'Replace your rankings?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'clear'
                ? `This moves all ${ranked.length} ranked players back to the pool. You can undo straight after.`
                : `This discards your current order (${ranked.length} ranked player${
                    ranked.length === 1 ? '' : 's'
                  }) and rebuilds the list as all players in ${RANK_LABEL} order. You can undo straight after.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button type="button" variant="outline" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmAction === 'clear' ? clearList : replaceAll}
            >
              {confirmAction === 'clear' ? 'Clear list' : 'Replace list'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save bar while there are unsaved changes: overlays the screen bottom
          on mobile (fixed, safe-area aware); sticky in the page flow on
          desktop. Cmd/Ctrl+S also saves. */}
      {!loadingData && dirty && (
        <div
          className={cn(
            'z-40 py-3 bg-background/95 backdrop-blur border-t border-neutral-200 flex items-center justify-between gap-3',
            isMobile ? 'fixed inset-x-0 bottom-0 px-4' : 'sticky bottom-0 mt-4 -mx-5 px-5'
          )}
          style={
            isMobile
              ? { paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }
              : undefined
          }
        >
          <p className="text-sm text-neutral-700">Unsaved changes</p>
          <Button onClick={handleSubmitRankings} disabled={loadingSubmit}>
            {loadingSubmit ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Save rankings'
            )}
          </Button>
        </div>
      )}
      {/* Hidden on mobile — it would overflow the zero-scroll layout, and the
          "Rankings saved" toast already confirms there. */}
      {!loadingData && !dirty && submitSuccess && (
        <p className="mt-4 text-sm text-brand-success hidden sm:flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" /> All changes saved
        </p>
      )}
    </div>
  );
}

CreatorRankings.propTypes = {
  user: PropTypes.object.isRequired,
};
